import { clsx } from 'clsx';
import dayjs, { Dayjs } from 'dayjs';
import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';

import {
  IconCalendar,
  IconArrowLeft,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';

interface IDatePickerProps {
  label?: string;
  value?: string | null;
  onChange: (date: string | null) => void;
  error?: string;
  isRequired?: boolean;
  disabled?: boolean;
  min?: string;
  max?: string;
  placeholder?: string;
  displayFormat?: string;
  showIcon?: boolean;
  containerClassName?: string;
  inputClassName?: string;
  dataTestId?: string;
}

type TViewMode = 'days' | 'months' | 'years';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

const YEARS_PER_PAGE = 9;

const DISABLED_CLASSES = 'text-neutral-disabled cursor-not-allowed';

const DatePicker: React.FC<IDatePickerProps> = ({
  label,
  value,
  onChange,
  error,
  isRequired,
  disabled,
  min,
  max,
  placeholder = 'Select date',
  displayFormat = 'MM/DD/YYYY',
  showIcon = true,
  containerClassName,
  inputClassName,
  dataTestId = 'date-picker',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<TViewMode>('days');
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(() =>
    value ? dayjs(value) : dayjs()
  );
  const [yearRangeStart, setYearRangeStart] = useState(
    () => Math.floor(dayjs().year() / YEARS_PER_PAGE) * YEARS_PER_PAGE
  );
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    openUpwards: false,
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Memoized parsed dates
  const selectedDate = useMemo(() => (value ? dayjs(value) : null), [value]);
  const minDate = useMemo(() => (min ? dayjs(min) : null), [min]);
  const maxDate = useMemo(() => (max ? dayjs(max) : null), [max]);
  const today = useMemo(() => dayjs().startOf('day'), []);

  // Validation functions
  const isDateDisabled = useCallback(
    (date: Dayjs): boolean => {
      if (disabled) return true;
      if (minDate && date.isBefore(minDate, 'day')) return true;
      if (maxDate && date.isAfter(maxDate, 'day')) return true;
      return false;
    },
    [disabled, minDate, maxDate]
  );

  const isMonthDisabled = useCallback(
    (monthIdx: number, year: number): boolean => {
      if (!minDate && !maxDate) return false;
      const testDate = dayjs().year(year).month(monthIdx);
      if (minDate && testDate.endOf('month').isBefore(minDate, 'day'))
        return true;
      if (maxDate && testDate.startOf('month').isAfter(maxDate, 'day'))
        return true;
      return false;
    },
    [minDate, maxDate]
  );

  const isYearDisabled = useCallback(
    (year: number): boolean => {
      if (minDate && year < minDate.year()) return true;
      if (maxDate && year > maxDate.year()) return true;
      return false;
    },
    [minDate, maxDate]
  );

  // Calculate and update dropdown position
  useLayoutEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpwards = spaceBelow < 345 && spaceAbove > spaceBelow;
      const GAP = 8;

      setDropdownPosition({
        top: openUpwards ? rect.top - 4 - GAP : rect.bottom + 4 + GAP,
        left: rect.left,
        openUpwards,
      });
    }
  }, [isOpen]);

  // Handle clicks outside and scroll
  useEffect(() => {
    if (!isOpen) return;

    const closeDropdown = () => {
      setIsOpen(false);
      setViewMode('days');
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        closeDropdown();
      }
    };

    const handleScroll = (e: Event) => {
      if (
        calendarRef.current &&
        e.target instanceof HTMLElement &&
        !calendarRef.current.contains(e.target)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  // Event handlers
  const handleDateClick = useCallback(
    (date: Dayjs) => {
      if (!isDateDisabled(date)) {
        onChange(date.format('YYYY-MM-DD'));
        setIsOpen(false);
      }
    },
    [isDateDisabled, onChange]
  );

  const handleYearClick = useCallback(
    (year: number) => {
      if (!isYearDisabled(year)) {
        setCurrentMonth((prev) => prev.year(year));
        setViewMode('months');
      }
    },
    [isYearDisabled]
  );

  const handleMonthClick = useCallback(
    (month: number) => {
      if (!isMonthDisabled(month, currentMonth.year())) {
        setCurrentMonth((prev) => prev.month(month));
        setViewMode('days');
      }
    },
    [isMonthDisabled, currentMonth]
  );

  const handleToday = useCallback(() => {
    if (!isDateDisabled(today)) {
      onChange(today.format('YYYY-MM-DD'));
      setCurrentMonth(today);
      setIsOpen(false);
    }
  }, [isDateDisabled, today, onChange]);

  const handleClear = useCallback(() => {
    onChange('');
    setIsOpen(false);
  }, [onChange]);

  const handleInputClick = useCallback(() => {
    if (!disabled) {
      const date = value ? dayjs(value) : dayjs();
      setCurrentMonth(date);
      setYearRangeStart(
        Math.floor(date.year() / YEARS_PER_PAGE) * YEARS_PER_PAGE
      );
      setIsOpen(true);
      setViewMode('days');
    }
  }, [disabled, value]);

  const handleNavigation = useCallback(
    (direction: 'prev' | 'next') => {
      if (viewMode === 'years') {
        setYearRangeStart((prev) =>
          direction === 'next' ? prev + YEARS_PER_PAGE : prev - YEARS_PER_PAGE
        );
      } else {
        const unit = viewMode === 'months' ? 'year' : 'month';
        setCurrentMonth((prev) =>
          direction === 'next' ? prev.add(1, unit) : prev.subtract(1, unit)
        );
      }
    },
    [viewMode]
  );

  // Memoized calendar days
  const calendarDays = useMemo(() => {
    const start = currentMonth.startOf('month').startOf('week');
    const end = currentMonth.endOf('month').endOf('week');
    const days: { date: Dayjs; isCurrentMonth: boolean }[] = [];
    const currentMonthNum = currentMonth.month();

    let day = start;
    while (day.isBefore(end) || day.isSame(end, 'day')) {
      days.push({
        date: day,
        isCurrentMonth: day.month() === currentMonthNum,
      });
      day = day.add(1, 'day');
    }
    return days;
  }, [currentMonth]);

  // Memoized year range
  const yearRange = useMemo(
    () =>
      Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearRangeStart - 4 + i),
    [yearRangeStart]
  );

  const formattedValue = selectedDate?.format(displayFormat) || '';

  const headerTitle = useMemo(() => {
    if (viewMode === 'years') {
      return `${yearRangeStart - 4} - ${yearRangeStart + 4}`;
    }
    if (viewMode === 'months') {
      return currentMonth.format('YYYY');
    }
    return currentMonth.format('MMMM YYYY');
  }, [viewMode, yearRangeStart, currentMonth]);

  const containerClasses = clsx(
    'relative flex items-center justify-between w-full px-3 py-2.5 border rounded-lg transition-all cursor-pointer',
    {
      'border-neutral-200 bg-neutral-50 focus-within:outline-none cursor-not-allowed! ':
        disabled,
      'border-red-500 focus-within:ring-2 focus-within:ring-red-100':
        !disabled && error,
      'border-neutral-200 hover:border-brand-default focus-within:outline-2 focus-within:outline-offset-2 focus-within:border-[#f7e8e2] focus-within:outline-[#f7e8e2] bg-white ':
        !disabled && !error,
    },
    containerClassName
  );

  const inputClasses = clsx(
    'w-full bg-transparent outline-none text-sm font-medium placeholder:text-neutral-disabled cursor-pointer',
    {
      'text-neutral-disabled cursor-not-allowed!': disabled,
      'text-neutral-900': !disabled,
    },
    inputClassName
  );

  return (
    <div className="relative w-full">
      {label && (
        <label className="block text-sm font-medium text-neutral-primary leading-5 mb-1.5">
          {label} {isRequired && <span className="text-red-500">*</span>}
        </label>
      )}

      <div
        className={containerClasses}
        onClick={handleInputClick}
        data-testid={dataTestId}
      >
        <input
          ref={inputRef}
          type="text"
          value={formattedValue}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          className={inputClasses}
          data-testid={`${dataTestId}-input`}
        />
        {showIcon && <IconCalendar size={20} className="text-neutral-400" />}
      </div>

      {error && (
        <p className="text-sm font-normal text-red-500 mt-1.5">{error}</p>
      )}

      {isOpen &&
        createPortal(
          <div
            ref={calendarRef}
            className="absolute z-9999 bg-white border border-neutral-200 rounded-xl shadow-2xl overflow-hidden"
            style={{
              top: dropdownPosition.openUpwards ? 'auto' : dropdownPosition.top,
              bottom: dropdownPosition.openUpwards
                ? window.innerHeight - dropdownPosition.top
                : 'auto',
              left: dropdownPosition.left,
              width: 250,
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between py-1 px-2 border-b border-neutral-200">
              {viewMode !== 'days' && (
                <button
                  onClick={() => setViewMode('days')}
                  className="p-2 hover:bg-neutral-100 rounded-lg transition"
                  aria-label="Back to days view"
                >
                  <IconArrowLeft size={20} color="#3D3D3D" />
                </button>
              )}

              <button
                onClick={() =>
                  setViewMode(viewMode === 'days' ? 'months' : 'years')
                }
                className="px-3 py-1.5 text-xs font-semibold text-neutral-primary hover:bg-neutral-100 rounded-lg transition"
              >
                {headerTitle}
              </button>

              <div className="flex gap-1">
                <button
                  onClick={() => handleNavigation('prev')}
                  className="p-2 hover:bg-neutral-100 rounded-lg"
                  aria-label="Previous"
                >
                  <IconChevronLeft size={18} />
                </button>
                <button
                  onClick={() => handleNavigation('next')}
                  className="p-2 hover:bg-neutral-100 rounded-lg"
                  aria-label="Next"
                >
                  <IconChevronRight size={18} />
                </button>
              </div>
            </div>

            {/* Views */}
            <div className="p-2.5">
              {viewMode === 'years' && (
                <div className="grid grid-cols-3 gap-3">
                  {yearRange.map((year) => {
                    const isSelected = currentMonth.year() === year;
                    const disabledYear = isYearDisabled(year);
                    const btnClasses = clsx(
                      'py-2 rounded-lg text-xs font-medium transition',
                      {
                        'bg-brand-default text-white':
                          isSelected && !disabledYear,
                        [DISABLED_CLASSES]: disabledYear,
                        'hover:bg-neutral-100 text-neutral-primary':
                          !isSelected && !disabledYear,
                      }
                    );
                    return (
                      <button
                        key={year}
                        disabled={disabledYear}
                        onClick={() => handleYearClick(year)}
                        className={btnClasses}
                      >
                        {year}
                      </button>
                    );
                  })}
                </div>
              )}

              {viewMode === 'months' && (
                <div className="grid grid-cols-3 gap-3">
                  {MONTHS.map((month, idx) => {
                    const isSelected = currentMonth.month() === idx;
                    const disabledMonth = isMonthDisabled(
                      idx,
                      currentMonth.year()
                    );
                    const btnClasses = clsx(
                      'py-2 rounded-lg text-xs font-medium transition',
                      {
                        'bg-brand-default text-white':
                          isSelected && !disabledMonth,
                        [DISABLED_CLASSES]: disabledMonth,
                        'hover:bg-neutral-100 text-neutral-primary':
                          !isSelected && !disabledMonth,
                      }
                    );
                    return (
                      <button
                        key={month}
                        disabled={disabledMonth}
                        onClick={() => handleMonthClick(idx)}
                        className={btnClasses}
                      >
                        {month.substring(0, 3)}
                      </button>
                    );
                  })}
                </div>
              )}

              {viewMode === 'days' && (
                <>
                  <div className="grid grid-cols-7 text-center text-xs font-semibold text-neutral-primary mb-2">
                    {WEEKDAYS.map((d, idx) => (
                      <div key={idx}>{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7">
                    {calendarDays.map((day, idx) => {
                      const isSelected = selectedDate?.isSame(day.date, 'day');
                      const isToday = day.date.isSame(today, 'day');
                      const disabledDay = isDateDisabled(day.date);

                      const btnClasses = clsx(
                        'aspect-square flex items-center justify-center rounded-lg text-xs transition',
                        {
                          'text-neutral-disabled': !day.isCurrentMonth,
                          [DISABLED_CLASSES]: day.isCurrentMonth && disabledDay,
                          'bg-brand-default text-white font-bold':
                            day.isCurrentMonth && !disabledDay && isSelected,
                          'text-brand-default font-bold':
                            day.isCurrentMonth &&
                            !disabledDay &&
                            !isSelected &&
                            isToday,
                          'hover:bg-neutral-100':
                            day.isCurrentMonth &&
                            !disabledDay &&
                            !isSelected &&
                            !isToday,
                        }
                      );

                      return (
                        <button
                          key={idx}
                          onClick={() => handleDateClick(day.date)}
                          disabled={disabledDay}
                          className={btnClasses}
                        >
                          {day.date.date()}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center p-3 border-t border-neutral-200">
              <button
                onClick={handleClear}
                className="text-xs font-medium text-neutral-primary hover:text-brand-default"
              >
                Clear
              </button>
              <button
                onClick={handleToday}
                className="text-xs font-medium text-brand-default hover:text-brand-default/80"
              >
                Today
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DatePicker;
